import SubscriptionPool from "../../redux/services/SubscriptionPool";
import createLogger from "../../utils/logger.util";
import ServiceSubscribeOptionsBase from "../../types/ServiceSubscribeOptionsBase";
import Subscription from "../../types/Subscription";
import { ModelEventConstructor } from "../../utils/ModelEventConstructor";
import Base from "../Base";

const logger = createLogger({ name: "InternalNotification" });

class InternalNotification extends Base {
	public static fromResponse(data: any): InternalNotification.Model {
		return {
			id: data.id,
			createdAt: data.createdAt,
			readAt: data.readAt ?? null,
			updatedAt: data.updatedAt,
			title: data.title ?? "",
			text: data.text ?? "",
			action: data.action,
			payload: data.payload ?? null,
		};
	}

	public static async subscribe(
		options: InternalNotification.SubscribeOptions,
		onUpdate: Subscription.OnUpdate<InternalNotification.Model>,
	): Promise<Subscription<InternalNotification.SubscribeOptions> | null> {
		const modelEventConstructor = new ModelEventConstructor({
			onUpdate: (state) => {
				onUpdate({
					...state,
					models: state.models.map(this.fromResponse),
				});
			},
		});

		const subscription = await SubscriptionPool.add(
			(prpc) =>
				prpc.theirsModel.notification.internal.subscribe({
					params: this.optionsToRequest(options),
					ping: () => true,
					onEvent: (events) => {
						try {
							modelEventConstructor.onEvent(events);
						} catch (err) {
							logger.warn("onEvent skipped:", err);
						}
					},
					onError: (error: Error) => {
						logger.error(error);
					},
				}),
			{ name: "InternalNotification.subscribe" },
		);

		return {
			unsubscribe: () => subscription.unsubscribe(),
			update: (opts: InternalNotification.SubscribeOptions) =>
				subscription.update(this.optionsToRequest(opts)),
		} as Subscription<InternalNotification.SubscribeOptions>;
	}

	private static optionsToRequest(
		options: InternalNotification.SubscribeOptions,
	) {
		return {
			limit: options.limit,
			offset: options.offset,
			unreadOnly: options.unreadOnly,
			taxiServiceIds: options.taxiServiceIds,
			order: options.order,
		};
	}

	public static async getAll(
		options: InternalNotification.GetAllOptions = {},
	) {
		try {
			const data = await this.request((prpc) =>
				prpc.theirsModel.notification.internal.getAll({
					limit: options.limit ?? 50,
					offset: options.offset ?? 0,
					unreadOnly: options.unreadOnly,
					taxiServiceIds: options.taxiServiceIds,
					order: options.order,
				}),
			);
			return (data?.items?.map(this.fromResponse) ??
				[]) as InternalNotification.Model[];
		} catch {
			return null;
		}
	}

	public static markRead(id: number) {
		return this.request((prpc) =>
			prpc.theirsModel.notification.internal.markRead({ id }),
		);
	}

	public static clearAll(params?: { taxiServiceIds?: number[] }) {
		return this.request((prpc) =>
			prpc.theirsModel.notification.internal.clearAll(params ?? {}),
		);
	}
}

declare namespace InternalNotification {
	interface Model {
		id: number;
		createdAt: string;
		readAt: string | null;
		updatedAt?: string;
		title: string;
		text: string;
		action: number;
		payload: {
			i18n?: {
				titleKey?: string;
				textKey?: string;
				params?: Record<string, string>;
			};
		} | null;
	}

	interface SubscribeOptions
		extends Omit<
			ServiceSubscribeOptionsBase<InternalNotification.Model>,
			"order" | "query"
		> {
		unreadOnly?: boolean;
		taxiServiceIds?: number[];
		order?: { createdAt?: "ASC" | "DESC" };
	}

	interface GetAllOptions {
		limit?: number;
		offset?: number;
		unreadOnly?: boolean;
		taxiServiceIds?: number[];
		order?: { createdAt?: "ASC" | "DESC" };
	}
}

export default InternalNotification;
